import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {Auth} from '../interfaces';
import {environment} from '@src/environments/environment';
import {isPlatformBrowser} from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {GoogleTagManagerService} from 'angular-google-tag-manager';
import Hotjar from '@hotjar/browser';
import {EMPTY} from "rxjs";
import {AccountConfig} from "@app/interfaces/config/account.config";
import {UserService} from "@app/services/userService/user.service";
import {ConfigAPIService} from "@app/services/api/config-api.service";
import {switchMap, take} from "rxjs/operators";
declare let fbq: any;

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private isAccountUnderControl = false;
  private lastAuth: Auth | null = null;
  private isBrowser: boolean;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private httpClient: HttpClient,
    private gtmService: GoogleTagManagerService,
    private configService: ConfigAPIService,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  public initClientSideTracking(): void {
    if (!this.isBrowser || this.isAccountUnderControl) return;

    try {
      if (this.isConfigured(environment.facebookTrackingID)) {
        fbq('init', environment.facebookTrackingID);
      }
    } catch (e) {
      console.error(e);
    }

    try {
      if (this.isConfigured(environment.hotjarTrackingID)) {
        this.setupHotjar(environment.hotjarTrackingID as unknown as number);
      }
    } catch (e) {
      console.error(e);
    }

    try {
      if (this.isConfigured(environment.gtmID)) {
        this.gtmService.addGtmToDom();
      }
    } catch (e) {
      console.error(e);
    }
  }

  setUser(auth: Auth | null) {
    if (this.isAccountUnderControl) return;

    this.lastAuth = auth;

    if (this.isBrowser) {
      if (auth) {
        this.isConfigured(environment.gtmID) && this.gtmService.pushTag({userId: auth.userUuid})
        Hotjar.isReady() && Hotjar.identify(auth.userUuid, {email: auth.email});
      } else {
        this.isConfigured(environment.gtmID) && this.gtmService.pushTag({userId: null})
      }
    }
  }

  setAccountConfig(accountConfig: AccountConfig) {
    if (this.isAccountUnderControl) return;

    if (this.isBrowser) {
      if (accountConfig) {
        this.isConfigured(environment.gtmID) && this.gtmService.pushTag({
          userId: this.lastAuth?.userUuid,
          accountUuid: accountConfig.uuid,
          package: accountConfig.activeAccountProductPackage?.productPackage?.name,
          isTrial: accountConfig.activeAccountProductPackage?.isTrial,
        })
        Hotjar.isReady() && Hotjar.identify
      } else {
        this.isConfigured(environment.gtmID) && this.gtmService.pushTag({
          userId: this.lastAuth?.userUuid,
          accountUuid: accountConfig.uuid,
          package: accountConfig.activeAccountProductPackage?.productPackage.name,
          isTrial: accountConfig.activeAccountProductPackage?.isTrial,
        })
      }
    }
  }

  firePageEvent(url: string, title: string) {
    if (this.isAccountUnderControl) return;

    if (this.isBrowser) {
      this.isConfigured(environment.gtmID) && this.gtmService.pushTag({event: 'PageView', page_title: title, page_path: url});
    }
  }

  fireBriefdAnalyticsEvent(event: string, data?: { [key: string]: any }) {
    if (this.isAccountUnderControl) return EMPTY;

    return this.configService.getAccountConfig().pipe(
      take(1),
      switchMap((config) => {
        const payload = {...(data ?? {}), accountUuid: config.uuid};

        return this.httpClient.post(`${environment.apiUrl}/api/analytics-events`, {event, data: payload});
      })
    );
  }

  public trackEvent(event: string, params: Object) {
    if (this.isAccountUnderControl) return;

    try {
      this.isConfigured(environment.gtmID) && this.gtmService.pushTag({event, params})
      this.isConfigured(environment.facebookTrackingID) && fbq('trackCustom', event, params);
      this.isConfigured(environment.hotjarTrackingID) && Hotjar.event(event);
    } catch (e) {
      console.error(e);
    }
  }

  private setupHotjar(hotjarTrackingId: number) {
    Hotjar.init(hotjarTrackingId, 6, {debug: !environment.production});
  }

  private isConfigured(environmentId: string | undefined): boolean {
    return environmentId !== undefined && environmentId !== 'x';
  }

  public setIsAccountControl(control: boolean) {
    this.isAccountUnderControl = control;
  }
}
